import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useMetaApiInstance } from '../api';
import { HOST_QUERY_KEY } from '../constants';

import type { AxiosError } from 'axios';
import type { UseMutationOptions } from '@tanstack/react-query';
import type { FavoriteService } from '../constants/types';

type AddFavoriteServicesOptions = UseMutationOptions<
  FavoriteService[],
  AxiosError,
  FavoriteService | FavoriteService[],
  FavoriteService[]
>;

/**
 * 즐겨찾는 서비스를 추가하는 훅입니다.
 * @description
 * - react-query로 mutate를 제공합니다.
 */
const useAddFavoriteService = (options?: AddFavoriteServicesOptions) => {
  const client = useQueryClient();
  const { addFavoriteService } = useMetaApiInstance();

  const mutation = async (favorite: FavoriteService | FavoriteService[]) => {
    const refinedFavoriteServices = Array.isArray(favorite)
      ? favorite.map(service => service.keyword)
      : [favorite.keyword];

    const param = { serviceKeywords: [...refinedFavoriteServices] };
    const response = await addFavoriteService(param);

    return response;
  };

  return useMutation({
    mutationKey: [HOST_QUERY_KEY.ADD_FAVORITE_SERVICE],
    mutationFn: mutation,
    onMutate: async newFavorite => {
      await client.cancelQueries({ queryKey: [HOST_QUERY_KEY.GET_FAVORITE_SERVICE] });

      const previousFavorites =
        client.getQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE]) || [];

      client.setQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], oldFavorites =>
        Array.isArray(newFavorite)
          ? [...(oldFavorites || []), ...newFavorite]
          : [...(oldFavorites || []), newFavorite],
      );

      return previousFavorites;
    },
    onSuccess: res => {
      const newFavorites = res || [];
      client.setQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], newFavorites);
    },
    onError: (error, newFavorite, previousFavorites) => {
      if (previousFavorites) {
        client.setQueryData([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], previousFavorites);
      }
    },
    ...options,
  });
};

export default useAddFavoriteService;
